import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from './NotificationProvider';
import Icon from './Icon';
import InsightsPanel, { InsightsPanelHandle } from './InsightsPanel';
import { entityPath } from '../utils/entityRoutes';
import '../styles/components/NotificationDropdown.css';

// A mention notification's entity_type is one of ans-backend's
// COMMENT_ENTITY_TYPES, not directly an EntityKind. Entity types whose
// screens were removed (guest, event, procurement order/shipment: their API
// routes are disabled) have no route, so those mentions are not linked
// rather than pointing at a dead page.
function mentionEntityPath(entityType: string | null | undefined, entityId: string | null | undefined): string | undefined {
  if (!entityId) return undefined;
  switch (entityType) {
    case 'vendor': return entityPath('vendor', entityId);
    case 'task': return entityPath('task', entityId);
    case 'shipping_agent': return entityPath('shippingAgent', entityId);
    case 'budget_item': return '/budget';
    default: return undefined;
  }
}

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ isOpen, onClose }) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotification, clearAllNotifications } = useNotifications();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const insightsRef = useRef<InsightsPanelHandle>(null);
  const navigate = useNavigate();
  // Insights render as rows inside the notification list, so "no notifications"
  // is only true when BOTH sources are empty.
  const [insightCount, setInsightCount] = useState(0);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success': return 'check';
      case 'error': return 'x';
      case 'warning': return 'warning';
      case 'info': default: return 'info';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success': return 'var(--success)';
      case 'error': return 'var(--danger)';
      case 'warning': return 'var(--warning)';
      case 'info': default: return 'var(--info)';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      <div className="notification-header">
        <h3>Notifications</h3>
        <div className="notification-actions">
          {(unreadCount > 0 || insightCount > 0) && (
            <button
              className="mark-all-read-btn"
              onClick={() => {
                markAllAsRead();
                void insightsRef.current?.dismissAll();
                onClose();
              }}
              title="Mark all as read"
            >
              Mark all read
            </button>
          )}
          {notifications.length > 0 && (
            <button
              className="clear-all-btn"
              onClick={() => {
                clearAllNotifications();
                onClose();
              }}
              title="Clear all notifications"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="notification-scroll">
        {/* Surfaces "what needs attention" insights (overdue tasks, pending
            guest portal orders, etc.) directly in the bell dropdown on every
            screen size. Used to also render inline on the Dashboard — moved
            here so it lives in one place, reachable from any page instead of
            only when the user happens to be on the Dashboard. */}
        <div className="notification-list">
          {/* No limit: each insight is meant to read as its own notification
              row, same as the ones below it — a "Show N more" button inside
              the dropdown read as a second, separate list bolted onto this
              one, not a natural continuation of it. */}
          <InsightsPanel
            ref={insightsRef}
            compact
            onNavigate={href => { onClose(); navigate(href); }}
            onCountChange={setInsightCount}
          />
          {notifications.length === 0 && insightCount === 0 ? (
            <div className="empty-notifications">
              <Icon name="bell" size={48} className="empty-icon" />
              <p>No notifications yet</p>
              <span>We'll notify you when there's something new</span>
            </div>
          ) : (
            notifications.map((notification) => {
              const href = mentionEntityPath(notification.entityType, notification.entityId);
              return (
              <div
                key={notification.id}
                className={`notification-item ${!notification.read ? 'unread' : ''}`}
                onClick={() => {
                  if (!notification.read) {
                    markAsRead(notification.id);
                  }
                  if (href) { onClose(); navigate(href); }
                }}
              >
                <div className="notification-icon">
                  <div style={ { color: getNotificationColor(notification.type) }}>
                    <Icon
                      name={getNotificationIcon(notification.type)}
                      size={16}
                    />
                  </div>
                </div>

                <div className="notification-content">
                  <p className="notification-message">{notification.message}</p>
                  <span className="notification-time">{formatTimestamp(notification.timestamp)}</span>
                </div>

                <div className="notification-actions">
                  {!notification.read && <div className="unread-indicator" />}
                  <button
                    className="clear-notification-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearNotification(notification.id);
                    }}
                    title="Clear notification"
                  >
                    <Icon name="x" size={12} />
                  </button>
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>

      {notifications.length > 0 && (
        <div className="notification-footer">
          <button
            className="view-all-btn"
            onClick={() => { onClose(); navigate('/activities'); }}
          >
            View All
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
